import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  Loader2,
  ShieldCheck
} from 'lucide-react';
import { apiRequest } from '../services/api';
import { TENANT } from '../config';
import { PRACTICE_OPTIONS } from '../config/practices';

type MeetingType = {
  key: string;
  label: string;
  duration_minutes: number;
  description?: string;
};

type Slot = {
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  meeting_type: string;
  label: string;
};

type CampaignPayload = {
  id: string;
  slug: string;
  event_name?: string;
  brand_name: string;
  public_title: string;
  public_subtitle?: string;
  platform_blurb?: string;
  primary_cta?: string;
  owner_name?: string;
  team_member?: string;
  campaign_url?: string;
  scan_id?: string;
  value_props?: string[];
  disclaimer?: string;
  hero_image_url?: string | null;
  conference_id?: string | null;
  fields?: { key: string; label: string; type: string; required?: boolean; options?: string[] }[];
  investor_types?: string[];
  investment_ranges?: string[];
  interest_options?: string[];
  meeting_types?: MeetingType[];
};

type FormState = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  investor_type: string;
  investment_range: string;
  primary_interest: string;
  how_met: string;
  consent_email: boolean;
  consent_sms: boolean;
};

const emptyForm: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  investor_type: '',
  investment_range: '',
  primary_interest: '',
  how_met: '',
  consent_email: true,
  consent_sms: false
};

const tenantTheme = TENANT.id === 'redex'
  ? {
      accent: '#DC2626',
      accentDark: '#991B1B',
      soft: '#FEF2F2',
      ink: '#111827',
      logo: TENANT.branding.logoPrimaryPath
    }
  : {
      accent: '#D39B2A',
      accentDark: '#242424',
      soft: '#FFF8E6',
      ink: '#242424',
      logo: TENANT.branding.logoPrimaryPath
    };

/**
 * The words this page says on its own, beyond what the funnel configures.
 *
 * They were written for a tenant that sells investments and shown to every
 * tenant: a nursing-home administrator who scanned a QR code at a conference
 * was told that "deal-room access remains pending review". A tenant that does
 * not sell investments must never read about deal rooms, investors or
 * securities here — the server's funnel copy makes the same promise for the
 * configured parts of the page and for the emails.
 */
const PAGE_COPY = TENANT.features.investing
  ? {
      formHeading: 'Request private access',
      bookingIntro: (name: string) => `Thanks, ${name}. Deal-room access remains pending review until the team approves it.`,
      confirmedNote: 'Deal-room access is still pending review. The team will approve or decline access separately after qualification.',
      defaultCta: 'Request Access & Book Intro',
      defaultMeetingType: 'investor_intro',
      unavailable: 'Event access unavailable'
    }
  : {
      formHeading: 'Request information',
      bookingIntro: (name: string) => `Thanks, ${name}. Pick a time that works for you.`,
      confirmedNote: 'A member of our team will be in touch before the call. Reply to the confirmation email if you need to change the time.',
      defaultCta: 'Request Info & Book a Call',
      defaultMeetingType: 'prospect_intro',
      unavailable: 'This event page is unavailable'
    };

function getQueryAttribution(searchParams: URLSearchParams) {
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'source', 'medium', 'campaign', 'team_member', 'owner', 'segment', 'qr'];
  return keys.reduce<Record<string, string>>((acc, key) => {
    const value = searchParams.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

function formatSlot(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

function groupSlots(slots: Slot[]) {
  return slots.reduce<Record<string, Slot[]>>((groups, slot) => {
    const key = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' }).format(new Date(slot.starts_at));
    groups[key] = groups[key] || [];
    groups[key].push(slot);
    return groups;
  }, {});
}

const EventDealRoom: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaign, setCampaign] = useState<CampaignPayload | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [leadId, setLeadId] = useState(searchParams.get('lead') || '');
  const [accessToken, setAccessToken] = useState(searchParams.get('token') || '');
  const [step, setStep] = useState<'form' | 'booking' | 'confirmed'>(searchParams.get('step') === 'booking' ? 'booking' : 'form');
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [confirmation, setConfirmation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  const attribution = useMemo(() => getQueryAttribution(searchParams), [searchParams]);
  const attributionQuery = useMemo(() => new URLSearchParams(attribution).toString(), [attribution]);

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError('');
    const query = attributionQuery;
    const response = await apiRequest<CampaignPayload>('GET', `/event-funnel/campaigns/${slug}${query ? `?${query}` : ''}`);
    if (!response.success || !response.data) {
      setError(response.message || response.error || 'This event campaign is not available.');
      setLoading(false);
      return;
    }
    setCampaign(response.data);
    setSelectedMeetingType(current => current || response.data?.meeting_types?.[0]?.key || PAGE_COPY.defaultMeetingType);
    setLoading(false);
  }, [attributionQuery, slug]);

  const loadSlots = useCallback(async (id: string, token: string, meetingType: string) => {
    if (!id || !token) return;
    setLoadingSlots(true);
    setError('');
    const response = await apiRequest<{ slots: Slot[]; meeting_types: MeetingType[] }>(
      'GET',
      `/event-funnel/leads/${id}/slots`,
      undefined,
      { params: { token, meeting_type: meetingType } }
    );
    if (!response.success || !response.data) {
      setError(response.message || response.error || 'Available times could not be loaded.');
      setSlots([]);
    } else {
      setSlots(response.data.slots || []);
      setSelectedSlot('');
    }
    setLoadingSlots(false);
  }, []);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (step === 'booking' && leadId && accessToken) {
      loadSlots(leadId, accessToken, selectedMeetingType);
    }
  }, [accessToken, leadId, loadSlots, selectedMeetingType, step]);

  /**
   * The fields this funnel presents.
   *
   * The server sends them; the fallback rebuilds the previous hard-coded form
   * from the option lists an older funnel already had, so nothing that existed
   * before this change renders differently because of it.
   */
  const configuredFields = useMemo(() => {
    if (campaign?.fields?.length) return campaign.fields;
    return [
      { key: 'investor_type', label: TENANT.features.investing ? 'Investor type' : 'Position', type: 'select', required: true, options: campaign?.investor_types || [] },
      { key: 'investment_range', label: TENANT.features.investing ? 'Investment range' : 'Facility / company name', type: 'select', required: true, options: campaign?.investment_ranges || [] },
      { key: 'primary_interest', label: 'Primary interest', type: 'select', required: true, options: campaign?.interest_options || PRACTICE_OPTIONS },
      { key: 'how_met', label: 'How did you meet the team?', type: 'textarea', required: false, options: [] },
    ];
  }, [campaign]);

  const updateField = (key: keyof FormState, value: string | boolean) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const submitLead = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!campaign) return;
    setSubmitting(true);
    setError('');
    const response = await apiRequest<any>('POST', `/event-funnel/campaigns/${campaign.slug}/leads`, {
      ...form,
      scan_id: campaign.scan_id,
      source: attribution.source || attribution.utm_source || 'event_qr',
      medium: attribution.medium || attribution.utm_medium || 'qr',
      owner: attribution.owner || campaign.owner_name || '',
      segment: attribution.segment || '',
      attribution
    });

    if (!response.success || !response.data) {
      setError(response.message || response.error || 'Your request could not be submitted.');
      setSubmitting(false);
      return;
    }

    const nextLeadId = response.data.lead_id;
    const nextToken = response.data.access_token;
    const nextType = response.data.recommended_meeting_type?.key || selectedMeetingType || campaign.meeting_types?.[0]?.key || PAGE_COPY.defaultMeetingType;
    setLeadId(nextLeadId);
    setAccessToken(nextToken);
    setSelectedMeetingType(nextType);
    setStep('booking');
    setSearchParams({ ...attribution, lead: nextLeadId, token: nextToken, step: 'booking' });
    setSubmitting(false);
  };

  const submitBooking = async () => {
    if (!leadId || !accessToken || !selectedSlot) return;
    setBooking(true);
    setError('');
    const response = await apiRequest<any>('POST', `/event-funnel/leads/${leadId}/bookings`, {
      access_token: accessToken,
      meeting_type: selectedMeetingType,
      starts_at: selectedSlot,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    });
    if (!response.success || !response.data) {
      setError(response.message || response.error || 'That time could not be booked.');
      setBooking(false);
      loadSlots(leadId, accessToken, selectedMeetingType);
      return;
    }
    setConfirmation(response.data);
    setStep('confirmed');
    setBooking(false);
  };

  const groupedSlots = useMemo(() => groupSlots(slots), [slots]);
  const firstName = form.first_name || 'there';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#242424]">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: tenantTheme.accent }} />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6 text-center">
        <div className="max-w-md">
          <ShieldCheck className="mx-auto h-10 w-10 mb-4" style={{ color: tenantTheme.accent }} />
          <h1 className="text-2xl font-semibold text-[#242424]">{PAGE_COPY.unavailable}</h1>
          <p className="mt-3 text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#242424]">
      <div className="grid min-h-screen lg:grid-cols-[0.88fr_1.12fr]">
        <section className="relative flex min-h-[420px] flex-col justify-between overflow-hidden bg-[#151515] px-6 py-7 text-white sm:px-10 lg:min-h-screen lg:px-12">
          <img
            /* The funnel's configured image wins; the tenant's own photograph
               is the fallback. It used to be `/HiveFund.jpg` for every tenant
               that was not REDEX, which put a Honeyshares real-estate photo
               behind eMerge's public conference page. */
            src={campaign.hero_image_url || TENANT.branding.eventHeroPath}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-44"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.74),rgba(0,0,0,0.38)_45%,rgba(0,0,0,0.82))]" />
          <div className="relative z-10 flex items-center justify-between gap-4">
            <img src={tenantTheme.logo} alt={TENANT.branding.name} className="max-h-10 max-w-[190px] object-contain" />
            <div className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur">
              {campaign.event_name || 'Private Event'}
            </div>
          </div>
          <div className="relative z-10 max-w-xl py-12 lg:py-0">
            <h1 className="text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
              {campaign.public_title}
            </h1>
            <p className="mt-5 max-w-lg text-base leading-7 text-white/86">
              {campaign.public_subtitle}
            </p>
          </div>
          <div className="relative z-10 grid gap-3 sm:grid-cols-3">
            {(campaign.value_props || []).slice(0, 3).map((item, index) => (
              <div key={item} className="border-t border-white/20 pt-3 text-sm leading-6 text-white/82">
                <span className="block text-xs font-semibold text-white/60">0{index + 1}</span>
                {item}
              </div>
            ))}
          </div>
        </section>

        <main className="flex items-center justify-center px-4 py-8 sm:px-8 lg:px-12">
          <div className="w-full max-w-3xl">
            <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-gray-600">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-sm">
                <Building2 className="h-4 w-4" style={{ color: tenantTheme.accent }} /> {TENANT.branding.name}
              </span>
            </div>

            {error && (
              <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {step === 'form' && (
              <form onSubmit={submitLead} className="rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-7">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-[#242424]">{PAGE_COPY.formHeading}</h2>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{campaign.platform_blurb}</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="text-sm font-medium text-gray-700">
                    First name
                    <input required value={form.first_name} onChange={e => updateField('first_name', e.target.value)} className="mt-1 h-11 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[#242424]" />
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Last name
                    <input required value={form.last_name} onChange={e => updateField('last_name', e.target.value)} className="mt-1 h-11 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[#242424]" />
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Email
                    <input required type="email" value={form.email} onChange={e => updateField('email', e.target.value)} className="mt-1 h-11 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[#242424]" />
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Phone
                    <input value={form.phone} onChange={e => updateField('phone', e.target.value)} className="mt-1 h-11 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[#242424]" />
                  </label>
                  {/* Rendered from the funnel's configuration, not hard-coded.
                      Label, control type, options and whether the field
                      appears at all are set per conference, because renaming
                      "Investor type" to "Position" is a content decision and
                      should not need a developer and a deploy.

                      The fallback reproduces the previous form exactly, so a
                      funnel created before this change looks unchanged. */}
                  {configuredFields.map(field => (
                    <label
                      key={field.key}
                      className={`text-sm font-medium text-gray-700${field.type === 'textarea' || field.key === 'primary_interest' ? ' sm:col-span-2' : ''}`}
                    >
                      {field.label}
                      {field.type === 'select' ? (
                        <select
                          required={field.required}
                          value={(form as any)[field.key] || ''}
                          onChange={e => updateField(field.key as keyof FormState, e.target.value)}
                          className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-[#242424]"
                        >
                          <option value="">Select one</option>
                          {/* A dropdown with nothing in it is a control the
                              visitor cannot answer, and it would still be
                              required. The server fills an empty one with the
                              practices; this repeats the guarantee so a stale
                              cached payload cannot strand a submission. */}
                          {(field.options?.length ? field.options : PRACTICE_OPTIONS)
                            .map(option => <option key={option} value={option}>{option}</option>)}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          required={field.required}
                          value={(form as any)[field.key] || ''}
                          onChange={e => updateField(field.key as keyof FormState, e.target.value)}
                          rows={3}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#242424]"
                        />
                      ) : (
                        <input
                          required={field.required}
                          value={(form as any)[field.key] || ''}
                          onChange={e => updateField(field.key as keyof FormState, e.target.value)}
                          className="mt-1 h-11 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[#242424]"
                        />
                      )}
                    </label>
                  ))}
                </div>

                <div className="mt-5 space-y-3 text-sm text-gray-700">
                  <label className="flex items-start gap-3">
                    <input type="checkbox" checked={form.consent_email} onChange={e => updateField('consent_email', e.target.checked)} className="mt-1" />
                    <span>Email me about this request and related event follow-up.</span>
                  </label>
                  <label className="flex items-start gap-3">
                    <input type="checkbox" checked={form.consent_sms} onChange={e => updateField('consent_sms', e.target.checked)} className="mt-1" />
                    <span>I agree to receive SMS follow-up about my scheduled intro call.</span>
                  </label>
                </div>

                <p className="mt-5 rounded-md px-4 py-3 text-xs leading-5 text-gray-700" style={{ backgroundColor: tenantTheme.soft }}>
                  {campaign.disclaimer}
                </p>

                <button disabled={submitting} type="submit" className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md px-5 text-sm font-semibold text-white transition disabled:opacity-60 sm:w-auto" style={{ backgroundColor: tenantTheme.accentDark }}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {campaign.primary_cta || PAGE_COPY.defaultCta}
                </button>
              </form>
            )}

            {step === 'booking' && (
              <section className="rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-7">
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-[#242424]">Book your intro call</h2>
                    <p className="mt-2 text-sm leading-6 text-gray-600">{PAGE_COPY.bookingIntro(firstName)}</p>
                  </div>
                  <div className="hidden rounded-md p-3 sm:block" style={{ backgroundColor: tenantTheme.soft }}>
                    <CalendarDays className="h-6 w-6" style={{ color: tenantTheme.accent }} />
                  </div>
                </div>

                <div className="mb-5 grid gap-3 sm:grid-cols-2">
                  {(campaign.meeting_types || []).map(type => (
                    <button
                      type="button"
                      key={type.key}
                      onClick={() => setSelectedMeetingType(type.key)}
                      className={`rounded-md border p-4 text-left transition ${selectedMeetingType === type.key ? 'border-[#242424] bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-400'}`}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-[#242424]">
                        <Clock className="h-4 w-4" style={{ color: tenantTheme.accent }} /> {type.label}
                      </div>
                      <p className="mt-2 text-xs leading-5 text-gray-600">{type.description}</p>
                      <p className="mt-2 text-xs font-medium text-gray-500">{type.duration_minutes} minutes</p>
                    </button>
                  ))}
                </div>

                {loadingSlots ? (
                  <div className="flex h-40 items-center justify-center rounded-md bg-gray-50">
                    <Loader2 className="h-6 w-6 animate-spin" style={{ color: tenantTheme.accent }} />
                  </div>
                ) : (
                  <div className="max-h-[420px] space-y-5 overflow-y-auto pr-1">
                    {Object.entries(groupedSlots).map(([day, daySlots]) => (
                      <div key={day}>
                        <h3 className="mb-2 text-sm font-semibold text-gray-700">{day}</h3>
                        <div className="grid gap-2 sm:grid-cols-3">
                          {daySlots.slice(0, 9).map(slot => (
                            <button
                              type="button"
                              key={slot.starts_at}
                              onClick={() => setSelectedSlot(slot.starts_at)}
                              className={`h-11 rounded-md border px-3 text-sm font-medium transition ${selectedSlot === slot.starts_at ? 'text-white' : 'bg-white text-[#242424] hover:border-gray-400'}`}
                              style={selectedSlot === slot.starts_at ? { backgroundColor: tenantTheme.accentDark, borderColor: tenantTheme.accentDark } : undefined}
                            >
                              {formatSlot(slot.starts_at).split(', ').slice(-1)[0]}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {!slots.length && <p className="rounded-md bg-gray-50 p-4 text-sm text-gray-600">No available times are currently open for this meeting type.</p>}
                  </div>
                )}

                <button disabled={!selectedSlot || booking} type="button" onClick={submitBooking} className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md px-5 text-sm font-semibold text-white transition disabled:opacity-60 sm:w-auto" style={{ backgroundColor: tenantTheme.accentDark }}>
                  {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
                  Confirm intro call
                </button>
              </section>
            )}

            {step === 'confirmed' && (
              <section className="rounded-lg border border-black/10 bg-white p-7 shadow-sm">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: tenantTheme.soft }}>
                  <CheckCircle2 className="h-7 w-7" style={{ color: tenantTheme.accent }} />
                </div>
                <h2 className="text-2xl font-semibold text-[#242424]">Your intro call is confirmed</h2>
                <p className="mt-3 text-sm leading-6 text-gray-600">
                  {confirmation?.formatted_time || 'Your selected time'} is on the calendar. A confirmation email and calendar file have been sent.
                </p>
                <p className="mt-4 rounded-md bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-700">
                  {PAGE_COPY.confirmedNote}
                </p>
                {confirmation?.add_to_calendar_url && (
                  <a href={confirmation.add_to_calendar_url} className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold text-white" style={{ backgroundColor: tenantTheme.accentDark }}>
                    <CalendarDays className="h-4 w-4" /> Add to calendar
                  </a>
                )}
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EventDealRoom;